import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../ui/button';
import { CheckCircle, XCircle, Receipt } from 'lucide-react';

export default function OrderConfirmation({ order, onReset, receiptPayload }) {
  useEffect(() => {
    // call printer if order is successful
    if (isSuccess) {
      async function printReceipt(receiptData) {
        const env = process.env.REACT_APP_ENV || "default";
        console.log("env", env);
        // don't print in local environment
        if (env === "default") {
          console.log("receiptData", JSON.stringify(receiptData));
          return;
        }

        const ip = window.location.hostname; // gets current host IP 
        const url = `http://localhost:8686/print`;
        try {
          await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(receiptData),
          });
        } catch (printError) {
          console.log("Error printing receipt:", printError);
        }
      }
      printReceipt(receiptPayload);
    }
    const timer = setTimeout(() => {
      onReset();
    }, 15000);

    return () => clearTimeout(timer);
  }, [onReset]);

  const isSuccess = Boolean(order?.data?.order);
  //const isSuccess = false // to simulate order failure screen 
  if (!isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="h-full flex flex-col items-center justify-center bg-white p-8"
      >
        {/* Failure Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="mb-8"
        >
          <div className="w-32 h-32 bg-red-500 rounded-full flex items-center justify-center shadow-2xl">
            <XCircle className="w-20 h-20 text-white" />
          </div>
        </motion.div>

        {/* Failure Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <h1 className="text-6xl font-bold text-gray-900 mb-4">
            Order Failed
          </h1>
          <p className="text-3xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
            Something went wrong while placing your order.
          </p>
          <p className="text-xl text-gray-500 mt-4">
            Please try again or contact staff for assistance.
          </p>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-8 text-gray-500 text-xl"
        >
          Returning to home screen...
        </motion.p>
        <Button
          variant="orange"
          size="home"
          className=" w-[415px] h-[85px] font-outfit font-medium rounded-[40px] px-6 py-3 mt-6"
          onClick={() => {
            onReset();
          }}
        >
          Return To Home
        </Button>
      </motion.div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="h-full flex flex-col items-center justify-center bg-white to-emerald-50 p-8"
    >
      {/* Success Icon */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        className="mb-8"
      >
        <div className="w-32 h-32 bg-green-500 rounded-full flex items-center justify-center shadow-2xl">
          <CheckCircle className="w-20 h-20 text-white" />
        </div>
      </motion.div>

      {/* Success Message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="text-center mb-12"
      >
        <h1 className="text-6xl font-bold text-gray-900 mb-4">Order Confirmed!</h1>
        <div className="bg-white rounded-2xl p-8 shadow-xl mb-6 inline-block">
          <p className="text-xl text-gray-600 mb-2">Your Order Number</p>
          <p className="text-6xl font-bold text-orange-600"> {order.data.order.kiosk_order_id}</p>
        </div>
        <p className="text-3xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
          Thank you for your order! We'll call your number when it's ready.
        </p>
      </motion.div>

      {/* Receipt Sent Message */}
      {(order.customer_email || order.customer_phone) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl p-6 shadow-lg flex items-center gap-4"
        >
          <Receipt className="w-8 h-8 text-green-600" />
          <div className="text-left">
            <p className="font-semibold text-gray-900 text-lg">Receipt Sent!</p>
            <p className="text-gray-600">
              {order.customer_email && `Email: ${order.customer_email}`}
              {order.customer_email && order.customer_phone && ' • '}
              {order.customer_phone && `Phone: ${order.customer_phone}`}
            </p>
          </div>
        </motion.div>
      )}

      {/* Decorative Elements */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
        className="absolute top-20 right-20 w-32 h-32 bg-green-200/30 rounded-full blur-2xl"
      />
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
        className="absolute bottom-20 left-20 w-40 h-40 bg-emerald-200/30 rounded-full blur-2xl"
      />

      {/* Auto-return message */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className=" text-gray-500 text-xl"
      >
        Returning to home screen...
      </motion.p>
      <div className="absolute bottom-20">
        <Button
          variant="orange"
          size="home"
          className=" w-[415px] h-[85px] font-outfit font-medium rounded-[40px] px-6 py-3 mt-6"
          onClick={() => {
            onReset();
          }}
        >
          Return To Home
        </Button>
      </div>
    </motion.div>

  );
}